import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { NotificationService, Notification } from '../services/notificationService';

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  loadNotifications: () => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAsUnread: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
  showNotification: (notification: Notification) => void;
  hideNotification: () => void;
  currentNotification: Notification | null;
  isNotificationVisible: boolean;
  showSuccess: (message: string, title?: string) => void;
  showError: (message: string, title?: string) => void;
  showWarning: (message: string, title?: string) => void;
  showInfo: (message: string, title?: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const { currentUser } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [currentNotification, setCurrentNotification] = useState<Notification | null>(null);
  const [isNotificationVisible, setIsNotificationVisible] = useState(false);

  const loadNotifications = useCallback(async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      const [userNotificationsResult, count] = await Promise.all([
        NotificationService.getUserNotifications(currentUser.uid, 10, 0),
        NotificationService.getUnreadCount(currentUser.uid)
      ]);

      setNotifications(userNotificationsResult.notifications);
      setUnreadCount(count);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  const markAsRead = async (notificationId: string) => {
    try {
      await NotificationService.markAsRead(notificationId);

      // Update local state
      setNotifications(prev =>
        prev.map(notification =>
          notification.id === notificationId
            ? { ...notification, isRead: true, readAt: new Date() as any }
            : notification
        )
      );

      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    if (!currentUser) return;

    try {
      await NotificationService.markAllAsRead(currentUser.uid);

      // Update local state
      setNotifications(prev =>
        prev.map(notification => ({
          ...notification,
          isRead: true,
          readAt: new Date() as any
        }))
      );

      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const markAsUnread = async (notificationId: string) => {
    try {
      // For marking as unread, we'll create a new notification with isRead: false
      // This is a workaround since Firestore doesn't support updating to false easily
      const notification = notifications.find(n => n.id === notificationId);
      if (notification && currentUser) {
        await NotificationService.createNotification({
          recipientId: currentUser.uid,
          title: notification.title,
          message: notification.message,
          type: notification.type,
          senderId: 'system',
          senderName: notification.senderName,
          expiresAt: notification.expiresAt ? (notification.expiresAt.toDate ? notification.expiresAt.toDate() : (typeof notification.expiresAt === 'number' ? new Date(notification.expiresAt) : new Date())) : undefined
        });

        // Reload notifications to get the updated data
        await loadNotifications();
      }
    } catch (error) {
      console.error('Error marking notification as unread:', error);
    }
  };

  const deleteNotification = async (notificationId: string) => {
    try {
      if (currentUser) {
        console.log('Soft deleting notification:', notificationId, 'for user:', currentUser.uid);
        await NotificationService.softDeleteNotification(notificationId, currentUser.uid);

        // Update local state - remove from user's view
        setNotifications(prev => {
          const filtered = prev.filter(notification => notification.id !== notificationId);
          console.log('Updated notifications count:', filtered.length);
          return filtered;
        });

        // Update unread count if the deleted notification was unread
        const deletedNotification = notifications.find(n => n.id === notificationId);
        if (deletedNotification && !deletedNotification.isRead) {
          setUnreadCount(prev => Math.max(0, prev - 1));
        }
        console.log('Notification deleted successfully:', notificationId);
      } else {
        console.error('No current user found for deletion');
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const hideNotification = useCallback(() => {
    setIsNotificationVisible(false);
    setCurrentNotification(null);
  }, []);

  const showNotification = useCallback((notification: Notification) => {
    setCurrentNotification(notification);
    setIsNotificationVisible(true);

    // Auto-hide after 5 seconds
    setTimeout(() => {
      hideNotification();
    }, 5000);
  }, [hideNotification]);

  const showSuccess = useCallback((message: string, title: string = 'Success') => {
    const notification: Notification = {
      id: `success-${Date.now()}`,
      title,
      message,
      type: 'success',
      recipientId: currentUser?.uid || 'system',
      senderId: 'system',
      senderName: 'System',
      createdAt: new Date() as any,
      isRead: false
    };
    showNotification(notification);
  }, [currentUser, showNotification]);

  const showError = useCallback((message: string, title: string = 'Error') => {
    const notification: Notification = {
      id: `error-${Date.now()}`,
      title,
      message,
      type: 'error',
      recipientId: currentUser?.uid || 'system',
      senderId: 'system',
      senderName: 'System',
      createdAt: new Date() as any,
      isRead: false
    };
    showNotification(notification);
  }, [currentUser, showNotification]);

  const showWarning = useCallback((message: string, title: string = 'Warning') => {
    const notification: Notification = {
      id: `warning-${Date.now()}`,
      title,
      message,
      type: 'warning',
      recipientId: currentUser?.uid || 'system',
      senderId: 'system',
      senderName: 'System',
      createdAt: new Date() as any,
      isRead: false
    };
    showNotification(notification);
  }, [currentUser, showNotification]);

  const showInfo = useCallback((message: string, title: string = 'Info') => {
    const notification: Notification = {
      id: `info-${Date.now()}`,
      title,
      message,
      type: 'info',
      recipientId: currentUser?.uid || 'system',
      senderId: 'system',
      senderName: 'System',
      createdAt: new Date() as any,
      isRead: false
    };
    showNotification(notification);
  }, [currentUser, showNotification]);

  // Load notifications when user changes
  useEffect(() => {
    if (currentUser) {
      loadNotifications();

      // Set up polling for new notifications every 30 seconds
      const interval = setInterval(loadNotifications, 30000);
      return () => clearInterval(interval);
    } else {
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [currentUser, loadNotifications]);

  const value: NotificationContextType = useMemo(() => ({
    notifications,
    unreadCount,
    loading,
    loadNotifications,
    markAsRead,
    markAsUnread,
    markAllAsRead,
    deleteNotification,
    showNotification,
    hideNotification,
    currentNotification,
    isNotificationVisible,
    showSuccess,
    showError,
    showWarning,
    showInfo
  }), [
    notifications,
    unreadCount,
    loading,
    loadNotifications,
    markAsRead,
    markAsUnread,
    markAllAsRead,
    deleteNotification,
    showNotification,
    hideNotification,
    currentNotification,
    isNotificationVisible,
    showSuccess,
    showError,
    showWarning,
    showInfo
  ]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};